import { useState } from 'react';

import type { DrillMode, TNode } from '../Node';
import type { DrillState } from './types';

export const useDrillMode = () => {
  const [drillState, setDrillState] = useState<DrillState[]>([]);

  const drillIntoNode: DrillMode['drillIntoNode'] = ({
    event,
    node,
    level,
  }) => {
    const target = event?.currentTarget;
    if (!target) return;

    // Use the row's vertical position but extend the right edge to the
    // tree container's edge so the panel appears flush (no scrollbar gap).
    const row = target.closest('[data-tree-row]');
    const rowRect = (row ?? target).getBoundingClientRect();

    // Only extend to the container's right edge for the root tree
    // (to close the scrollbar gap). For nested drills inside floating
    // panels, the row's natural rect preserves spacing from padding.
    const isInsideFloatingPanel = !!target.closest('[data-floating-panel]');
    const rootContainer = isInsideFloatingPanel
      ? null
      : target.closest('[data-tree-root]');
    const containerRight =
      rootContainer?.getBoundingClientRect().right ?? rowRect.right;

    const anchorRowRect = new DOMRect(
      rowRect.x,
      rowRect.y,
      containerRight - rowRect.x,
      rowRect.height
    );

    setDrillState((prev) => [
      ...prev.slice(0, level),
      { level, parentNode: node, anchorRowRect },
    ]);
  };

  const drillOutToLevel = (level: number) => {
    setDrillState((prev) => prev.slice(0, level));
  };

  const isDrilledInto = (node: TNode) => {
    const depth = node.data.depth;
    return depth !== undefined && drillState[depth]?.parentNode?.id === node.id;
  };

  const clearDrillState = () => setDrillState([]);

  return {
    drillState,
    drillIntoNode,
    drillOutToLevel,
    isDrilledInto,
    clearDrillState,
  };
};
